import { useState } from "react";
import {
  Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { auditApi, type AuditLogDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

/**
 * UC-M1-AUD-01 — audit search + CSV export. Server-side filters mirror the AuditLog query
 * shape from AUDIT_LOGGING.md §1. Export is the visible page only — full export goes via
 * a Final-phase Hangfire job.
 */
export function AuditLogPage() {
  const canExport = useAuth(s => s.hasPermission("audit.log.export"));
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 50 });

  const query = useQuery({
    queryKey: ["audit-logs", action, entityType, fromDate, toDate, pagination.page, pagination.pageSize],
    queryFn: () => auditApi.list({
      action: action || undefined,
      entityType: entityType || undefined,
      fromDate: fromDate || undefined, toDate: toDate || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const columns: GridColDef<AuditLogDto>[] = [
    { field: "occurredAt", headerName: "When", width: 180,
      valueFormatter: (v) => v ? new Date(v as string).toLocaleString() : "" },
    { field: "action", headerName: "Action", width: 220 },
    { field: "entityType", headerName: "Entity", width: 160 },
    { field: "entityId", headerName: "Entity id", width: 220,
      valueFormatter: (v) => v ? String(v).slice(0, 8) + "…" : "" },
    { field: "userId", headerName: "User", width: 220,
      valueFormatter: (v) => v ? String(v).slice(0, 8) + "…" : "" },
    { field: "correlationId", headerName: "Correlation", width: 140,
      valueFormatter: (v) => v ? String(v).slice(0, 8) + "…" : "" },
    { field: "ipAddress", headerName: "IP", width: 140 },
  ];

  const exportCsv = () => {
    if (!query.data) return;
    const header = "OccurredAt,Action,EntityType,EntityId,UserId,CorrelationId,IpAddress";
    const rows = query.data.items.map(r => [
      r.occurredAt, r.action, r.entityType ?? "", r.entityId ?? "",
      r.userId ?? "", r.correlationId ?? "", r.ipAddress ?? "",
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `audit-log-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Audit log</Typography>
        {canExport && (
          <Button startIcon={<DownloadIcon />} variant="outlined"
            disabled={!query.data || query.data.items.length === 0}
            onClick={exportCsv}>
            Export current page (CSV)
          </Button>
        )}
      </Stack>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
          <TextField label="Action" size="small" sx={{ minWidth: 180 }}
            select value={action} onChange={(e) => setAction(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="login.success">login.success</MenuItem>
            <MenuItem value="login.failed">login.failed</MenuItem>
            <MenuItem value="po.submitted">po.submitted</MenuItem>
            <MenuItem value="po.approved">po.approved</MenuItem>
            <MenuItem value="grn.posted">grn.posted</MenuItem>
            <MenuItem value="pi.posted">pi.posted</MenuItem>
            <MenuItem value="si.posted">si.posted</MenuItem>
            <MenuItem value="si.cancelled">si.cancelled</MenuItem>
            <MenuItem value="substitution.applied">substitution.applied</MenuItem>
            <MenuItem value="journal.posted">journal.posted</MenuItem>
            <MenuItem value="period.closed">period.closed</MenuItem>
            <MenuItem value="period.reopened">period.reopened</MenuItem>
          </TextField>
          <TextField label="Entity type" size="small" sx={{ minWidth: 200 }}
            value={entityType} onChange={(e) => setEntityType(e.target.value)} />
          <TextField label="From" type="date" size="small"
            InputLabelProps={{ shrink: true }}
            value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <TextField label="To" type="date" size="small"
            InputLabelProps={{ shrink: true }}
            value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []} columns={columns} loading={query.isLoading}
            paginationMode="server" rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination} onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
          />
        </Box>
      </Paper>
    </Box>
  );
}
